import { useCallback, useEffect, useRef, useState } from "react";
import { apiGet } from "../lib/api";
import type { Phase1JobPoll } from "../lib/phase1Job";
import { formatApiError } from "../lib/utils";

const POLL_MS = 3500;
const POLL_MS_ON_ERROR = 10000;

export type Phase1RunPollResult = {
  running: boolean;
  jobStatus: string;
  statusDetail: string;
  progress: Phase1JobPoll["progress"] | null;
  liveLog: Phase1JobPoll["log"] | null;
  scrapeSteps: Phase1JobPoll["scrape_steps"];
  jobId: string;
  error: string;
  start: (runId: string) => void;
  stop: () => void;
};

export function usePhase1RunPoll(
  onComplete?: (data: Phase1JobPoll) => void | Promise<void>,
  onError?: (message: string) => void,
): Phase1RunPollResult {
  const [running, setRunning] = useState(false);
  const [jobStatus, setJobStatus] = useState("");
  const [statusDetail, setStatusDetail] = useState("");
  const [progress, setProgress] = useState<Phase1JobPoll["progress"] | null>(null);
  const [liveLog, setLiveLog] = useState<Phase1JobPoll["log"] | null>(null);
  const [scrapeSteps, setScrapeSteps] = useState<Phase1JobPoll["scrape_steps"]>([]);
  const [jobId, setJobId] = useState("");
  const [error, setError] = useState("");
  const pollRef = useRef<number | null>(null);
  const runIdRef = useRef("");
  const pollDelayRef = useRef(POLL_MS);
  const onCompleteRef = useRef(onComplete);
  const onErrorRef = useRef(onError);
  onCompleteRef.current = onComplete;
  onErrorRef.current = onError;

  const stop = useCallback(() => {
    if (pollRef.current) {
      window.clearInterval(pollRef.current);
      pollRef.current = null;
    }
    runIdRef.current = "";
    pollDelayRef.current = POLL_MS;
  }, []);

  const pollOnce = useCallback(
    async (rid: string) => {
      try {
        const data = await apiGet<Phase1JobPoll>(
          `/api/jobs/${encodeURIComponent(rid)}?lite=true`,
        );
        pollDelayRef.current = POLL_MS;
        setError("");
        setJobStatus(data.status || "");
        setStatusDetail(String(data.status_detail || ""));
        setProgress(data.progress || null);
        setLiveLog(data.log || null);
        const steps = data.scrape_steps || data.log?.scrape_steps;
        if (steps?.length) setScrapeSteps(steps);

        const isRunning = ["pending", "running"].includes(data.status);
        setRunning(isRunning);

        if (data.job_id) setJobId(data.job_id);

        if (
          data.status === "ok" ||
          data.status === "no_urls" ||
          data.status === "skipped_duplicate"
        ) {
          stop();
          setRunning(false);
          await onCompleteRef.current?.(data);
          return true;
        }
        if (data.status === "cancelled") {
          stop();
          setRunning(false);
          setError(data.error || "Phase 1 stopped");
          onErrorRef.current?.(data.error || "Phase 1 stopped");
          return true;
        }
        if (data.status === "error" || data.status === "interrupted") {
          stop();
          setRunning(false);
          const msg = data.error || "Phase 1 failed";
          setError(msg);
          onErrorRef.current?.(msg);
          return true;
        }
        return false;
      } catch (e) {
        pollDelayRef.current = POLL_MS_ON_ERROR;
        if (pollRef.current) {
          window.clearInterval(pollRef.current);
        }
        pollRef.current = window.setInterval(() => {
          void pollOnce(rid);
        }, pollDelayRef.current);
        const msg = formatApiError(e);
        setError(msg);
        setRunning(true);
        return false;
      }
    },
    [stop],
  );

  const start = useCallback(
    (rid: string) => {
      if (!rid) return;
      if (runIdRef.current === rid && pollRef.current) {
        return;
      }
      if (pollRef.current) {
        window.clearInterval(pollRef.current);
        pollRef.current = null;
      }
      const isNewRun = runIdRef.current !== rid;
      runIdRef.current = rid;
      pollDelayRef.current = POLL_MS;
      setRunning(true);
      if (isNewRun) {
        setError("");
        setProgress(null);
        setScrapeSteps([]);
      }
      void pollOnce(rid).then(() => {
        if (runIdRef.current !== rid) return;
        if (pollRef.current) return;
        pollRef.current = window.setInterval(() => {
          void pollOnce(rid);
        }, pollDelayRef.current);
      });
    },
    [pollOnce],
  );

  useEffect(() => () => stop(), [stop]);

  return {
    running,
    jobStatus,
    statusDetail,
    progress,
    liveLog,
    scrapeSteps: scrapeSteps || [],
    jobId,
    error,
    start,
    stop,
  };
}
